import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiService, TimeoutError } from '@services/api';
import { CornerDecoration, LoadingOverlay, Button } from '@components/common';
import { AmountPicker, DonationForm, DonationInfo } from '@components/donation';
import goldImage from '@assets/images/gold.png';
import './DonatePage.css';

type PaymentStatus = 'success' | 'fail' | 'unknown' | null;

interface TimeoutPopupProps {
  onClose: () => void;
}

const TimeoutPopup: React.FC<TimeoutPopupProps> = ({ onClose }) => (
  <div className="timeout-popup-overlay">
    <div className="timeout-popup">
      <div className="timeout-popup__icon">⚠️</div>
      <h2 className="timeout-popup__title">Unusual Loading Time</h2>
      <p className="timeout-popup__message">
        The server is experiencing unusual loading times. Please try again later
        or contact the administrator if the issue persists.
      </p>
      <div className="timeout-popup__actions">
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  </div>
);

const DonationDisplay: React.FC = () => {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [showTimeoutPopup, setShowTimeoutPopup] = useState(false);

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
  };

  const handleBack = () => {
    setSelectedAmount(null);
  };

  const handleCloseTimeoutPopup = () => {
    setShowTimeoutPopup(false);
    setSelectedAmount(null);
  };

  const handleDonation = async (email: string, discord: string | undefined, amount: number) => {
    setLoading(true);

    try {
      const response = await apiService.initiateDonation({
        email,
        amount,
        discord: discord || '',
      });

      if (!response.success || !response.redirectUrl) {
        throw new Error(response.error || 'Failed to initiate donation');
      }

      toast.success('Redirecting to payment...');

      setTimeout(() => {
        window.location.href = response.redirectUrl!;
      }, 1500);
    } catch (error) {
      console.error('Donation error:', error);

      if (error instanceof TimeoutError) {
        setLoading(false);
        setShowTimeoutPopup(true);
      } else {
        toast.error(
          error instanceof Error
            ? error.message
            : 'Failed to process donation. Please try again.'
        );
        setLoading(false);
      }
    }
  };

  return (
    <>
      {loading && <LoadingOverlay text="Redirecting to payment..." />}
      {showTimeoutPopup && <TimeoutPopup onClose={handleCloseTimeoutPopup} />}
      <section className="page-section donation-section">
        <div className="donation-container">
          <Link to="/" className="back-home-link">
            ← Back to Home
          </Link>

          <img
            src={goldImage}
            alt="Support Our Cause"
            className="donation-image"
          />

          <h1 className="page-title">Support Our Cause</h1>

          {selectedAmount ? (
            <DonationForm
              amount={selectedAmount}
              onSubmit={handleDonation}
              onBack={handleBack}
              loading={loading}
            />
          ) : (
            <>
              <p className="page-subtitle">Choose your donation amount</p>
              <AmountPicker onSelect={handleAmountSelect} />
            </>
          )}

          <DonationInfo />
        </div>

        <CornerDecoration />
      </section>
    </>
  );
};

interface MessageProps {
  message: string;
  countdown: number | null;
}

const Message: React.FC<MessageProps> = ({ message, countdown }) => (
  <section className="page-section donation-section">
    <p className="message-box">{message}</p>
    {countdown !== null && (
      <p className="countdown-text">
        Redirecting to homepage in {countdown} seconds...
      </p>
    )}
    <CornerDecoration />
  </section>
);

const DonatePage: React.FC = () => {
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState<number | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const paymentStatus = searchParams.get('payment') as PaymentStatus;
    const hasSuccess = searchParams.get('success');
    const hasCanceled = searchParams.get('canceled');

    if (paymentStatus === 'success' || hasSuccess) {
      setMessage(
        'Thank you for your generous donation! You will receive an email confirmation.'
      );
      setCountdown(10);
      toast.success('Payment successful! Thank you for your support!');
    } else if (paymentStatus === 'fail' || hasCanceled) {
      setMessage(
        "Donation canceled. Your support means the world to us - come back anytime you're ready."
      );
      setCountdown(10);
      toast.warning('Payment was canceled or failed');
    } else if (paymentStatus === 'unknown') {
      setMessage(
        "We couldn't verify your payment status. Please contact support if you completed a payment."
      );
      setCountdown(10);
      toast.error('Payment status unknown');
    }
  }, [searchParams]);

  useEffect(() => {
    if (countdown === null) return;

    if (countdown === 0) {
      navigate('/');
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, navigate]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      {message ? (
        <Message message={message} countdown={countdown} />
      ) : (
        <DonationDisplay />
      )}
    </>
  );
};

export default DonatePage;
